import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import axios from "../../../../../api/axios";
import { languageContext } from "../../../../../context/languageContext";
import { useContext, useEffect, useState } from "react";

export const useSurveyQuestions = () => {
    let { userId, customer } = useParams();
    customer = customer.toLowerCase();

    const language = useContext(languageContext);
    const [surveyQuestions, setSurveyQuestions] = useState(null);
    const queryClient = useQueryClient();

    //#region Get survey info questions
    const { data: surveyInfo, isLoading: isLoadingQuestions, isError: isErrorQuestions, isFetching: isFetchingQuestions } = useQuery({
        queryKey: ["surveyInfo", userId],
        queryFn: async () => {
            const response = await axios.get(`userquestions/${userId}?customer=${customer}&lang=${language.locale}`);
            return response.data;
        },
        refetchOnWindowFocus: false,
        retry: 0,
    });
    const questionsNumber = (surveyInfo?.personalInfo || surveyInfo?.requiredPersonalInfo) ? surveyInfo?.questions?.length + 1 || 0 : surveyInfo?.questions?.length || 0;

    useEffect(() => {
        if (surveyInfo) {
            setSurveyQuestions(surveyInfo.questions);
        }
    }, [surveyInfo]);
    //#endregion

    //#region Save answers
    /**
     * Mutation hook to save survey answers.
     * 
     * This hook uses `useMutation` from React Query to handle the process of saving survey answers.
     * It sends a POST request to the `answer` endpoint with the provided values and customer information.
     * 
     * @constant
     * @type {object}
     * @property {function} mutationFn - The function to execute the mutation. It sends a POST request with the survey answers.
     * @property {function} onSuccess - Callback function executed when the mutation is successful. It invalidates the `surveyInfo` query for the current user.
     * @property {function} onError - Callback function executed when the mutation fails. It handles different error statuses and logs the error.
     * 
     * @param {object} values - The survey answers to be saved.
     * @param {string} customer - The customer identifier.
     * @param {string} userId - The user identifier.
     * 
     * @returns {number} - The status code of the response.
     */
    const saveAnswersMutation = useMutation({
        mutationFn: async (values) => {
            const response = await axios.post(`answer?customer=${customer}`, values);
            return response.status;
        },
        onError: (error) => {
            if (error.response.status === 400) {
                console.error(error);
            } else if (error.response.status === 409) {
                // console.error(error.response?.data?.text);
                console.error(error);
            } else {
                console.error(error);
            }
        }
    });
    //#endregion

    //#region Update user information
    /**
     * Mutation hook to update user information.
     * 
     * @constant
     * @type {Object}
     * @property {Function} mutationFn - The function to perform the mutation. Sends a PUT request to update user data.
     * @property {Function} onSuccess - Callback function executed on successful mutation. Redirects to the main survey page and removes user answers from local storage.
     * @property {Function} onError - Callback function executed on mutation error. Logs the error to the console.
     * @property {Function} onSettled - Callback function executed when the mutation is either successfully completed or encounters an error. Sets the sending state to false.
     */
    const updateUserMutation = useMutation({
        mutationFn: async (values) => {
            const response = await axios.put(`users/${userId}?customer=${customer}`, values);
            return response.status;
        },
        onSuccess: (data, values) => {
            //TODO: Redirect to main survey page
            //delete answers from local storage
            window.localStorage.removeItem(userId);
            queryClient.invalidateQueries({ queryKey: ["surveyInfo", userId], exact: true });
        },
        onError: (error) => {
            if (error.response.status === 400) {
                console.error(error);
            } else {
                console.error(error);
            }
        }
    });
    //#endregion

    //#region Get and Save IP info
    const getIpInfo = async () => {
        try {
            const responseIp = await axios.get(`https://api.ipify.org/?format=json`);
            const ip = responseIp.data;
            // Split the IP by comma and get the first part.
            const userIp = ip?.ip?.split?.(',')[0].trim() || ip?.ip.trim();
            const responseInfo = await axios.get(`https://ipwho.is/${userIp}`);
            const ipInfo = responseInfo.status === 200 ? responseInfo.data : null;
            const logIp = {
                user_id: userId,
                country_name: ipInfo?.country,
                city: ipInfo?.city,
                postal: ipInfo?.postal,
                latitude: ipInfo?.latitude,
                longitude: ipInfo?.longitude,
                ip: userIp,
                state: ipInfo?.region
            };
            saveipInfo.mutateAsync(logIp);
        } catch (error) {
            // console.error("Error fetching IP info:", error);
            const logIp = {
                user_id: userId,
                country_name: null,
                city: null,
                postal: null,
                latitude: null,
                longitude: null,
                ip: null,
                state: null
            };
            saveipInfo.mutateAsync(logIp);
        }
    };

    const saveipInfo = useMutation({
        mutationFn: async (values) => {
            const response = await axios.post(`log?customer=${customer}`, values);
            return response.status;
        },
        retry: 2,
        retryDelay: 1000,
    });
    //#endregion

    const getLanguageOptions = (question, language, questionType = null) => {
        const languageLabel = language === 'es' ? 'options' : 'options' + language;
        //check if all the options are null
        const checkAllNullOptions = question?.[languageLabel].every(option => option === null);
        const options = checkAllNullOptions ? question?.options : question?.[languageLabel];
        if (questionType === "select") {
            //Cuando son tipo select radio o check da problema en el autocomplete si tiene opciones nulas
            return options.filter(option => option === null).length > 0 ? question.options : options;
        }
        return options;
    }

    return {
        surveyInfo,
        surveyQuestions,
        setSurveyQuestions,
        questionsNumber,
        isLoadingQuestions,
        isErrorQuestions,
        isFetchingQuestions,
        saveAnswersMutation,
        updateUserMutation,
        getIpInfo,
        saveipInfo,
        getLanguageOptions
    }; // Exportar variables
}
